import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DocumentMeta from 'react-document-meta';
import {
  IoAlertCircleOutline,
  IoCalendarOutline,
  IoCheckmarkCircleOutline,
  IoChevronDown,
  IoChevronForward,
  IoCompassOutline,
  IoCutOutline,
  IoHeartOutline,
  IoHomeOutline,
  IoLeafOutline,
  IoPeopleOutline,
  IoSparklesOutline,
  IoStarOutline,
  IoWarningOutline,
} from 'react-icons/io5';
import PersonalityHeader from '../components/Header';
import SideCharacters from '../components/SideCharacters';
import { findCharacter } from '../data/characters';
import { traitsForType } from '../data/scoring';
import { CompatibilityMatch, typeTabContent } from '../data/typeTabContent';
import Styles from './styles.module.css';

const GROUP_ACCENTS = [
  { tint: '#FDECF1', accent: '#E44E70' },
  { tint: '#E6F4EA', accent: '#3D9A5C' },
  { tint: '#E7F0FB', accent: '#3E7BC4' },
  { tint: '#FBF0DE', accent: '#C98A2E' },
];
const accentFor = (i: number) => GROUP_ACCENTS[i % GROUP_ACCENTS.length];

const SectionCard: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <div className={Styles.section}>
    <div className={Styles.sectionHead}>
      {icon}
      <span>{title}</span>
    </div>
    {children}
  </div>
);

const PersonalityAccordion: React.FC<{ title: string; paragraphs: string[]; tint: string; accent: string }> = ({
  title, paragraphs, tint, accent,
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={Styles.accordionCard}>
      <button type="button" className={Styles.accordionHead} onClick={() => setOpen((v) => !v)}>
        <span className={Styles.accordionIcon} style={{ backgroundColor: tint, color: accent }}><IoSparklesOutline size={15} /></span>
        <span className={Styles.accordionTitle}>{title}</span>
        <span className={Styles.accordionChevron} style={{ transform: open ? 'rotate(180deg)' : 'none' }}>
          <IoChevronDown size={16} color="#BBBBBB" />
        </span>
      </button>
      {open && (
        <div className={Styles.accordionBody}>
          {paragraphs.map((p, i) => (
            <p key={i} className={Styles.paragraph}>{p}</p>
          ))}
        </div>
      )}
    </div>
  );
};

const CompatibilityCard: React.FC<{ match: CompatibilityMatch; tint: string; accent: string; onTap: (type: string) => void }> = ({
  match, tint, accent, onTap,
}) => {
  const matchedCharacter = findCharacter(match.type);
  return (
    <button type="button" className={Styles.compatCard} onClick={() => onTap(match.type)}>
      <div className={Styles.compatHead}>
        {matchedCharacter && <img src={matchedCharacter.imagePath} alt={match.type} className={Styles.compatAvatar} style={{ backgroundColor: tint }} />}
        <span className={Styles.compatBadge} style={{ backgroundColor: accent }}>{match.type}</span>
        <span className={Styles.compatSubtitle}>{match.subtitle}</span>
        <IoChevronForward size={18} color={accent} />
      </div>
      {match.note && (
        <span className={Styles.compatNote} style={{ backgroundColor: tint, color: accent }}>{match.note}</span>
      )}
      {match.paragraphs.map((p, i) => (
        <p key={i} className={Styles.compatParagraph}>{p}</p>
      ))}
    </button>
  );
};

const PersonalResult = () => {
  const { type = '' } = useParams<{ type: string }>();
  const navigate = useNavigate();

  const character = findCharacter(type.toUpperCase());
  const traits = useMemo(() => traitsForType(type.toUpperCase()), [type]);
  const content = character ? typeTabContent[character.title] : undefined;

  // 診断結果からの遷移・キャラクター一覧からのタップ・相性カードからの
  // 別タイプへの遷移、いずれもこの画面に来たら必ず一番上から見せる。
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [type]);

  const openIntroduce = (targetType: string) => {
    navigate(`/personality/result/${targetType}`, { state: { introduce: true } });
  };

  const meta = {
    title: character ? `${character.title} ${character.subtitle} | 性格診断 | memorii works` : '性格診断 | memorii works',
    description: character ? character.catchphrase : '美容師像の性格診断結果ページです。',
    canonical: `https://memorii-works.com/personality/result/${type}`,
    meta: { charset: 'utf-8' },
  };

  if (!character) {
    return (
      <DocumentMeta {...meta}>
        <PersonalityHeader title="性格診断" mode="close" />
        <div className={Styles.notFound}>
          <p>タイプが見つかりませんでした。</p>
          <button type="button" onClick={() => navigate('/personality')}>診断トップへ戻る</button>
        </div>
      </DocumentMeta>
    );
  }

  return (
    <DocumentMeta {...meta}>
      <PersonalityHeader title="性格診断" mode="back" />
      <SideCharacters imagePath={character.imagePath} />
      <div className={Styles.page}>
        <div className={Styles.hero}>
          <img src={character.heroPath} alt={character.subtitle} className={Styles.heroImage} />
        </div>
        <div className={Styles.heroBody}>
          <span className={Styles.typeBadge}>{character.title}</span>
          <h1 className={Styles.subtitle}>{character.subtitle}</h1>
          {character.catchphrase && <p className={Styles.catchphrase}>{character.catchphrase}</p>}
          {traits.length >= 4 && (
            <div className={Styles.traitGrid}>
              <div className={Styles.traitChip}><span className={Styles.traitIcon}><IoPeopleOutline size={15} /></span>{traits[0]}</div>
              <div className={Styles.traitChip}><span className={Styles.traitIcon}><IoCalendarOutline size={15} /></span>{traits[3]}</div>
              <div className={Styles.traitChip}><span className={Styles.traitIcon}><IoHeartOutline size={15} /></span>{traits[1]}</div>
              <div className={Styles.traitChip}><span className={Styles.traitIcon}><IoLeafOutline size={15} /></span>{traits[2]}</div>
            </div>
          )}
        </div>

        <div className={Styles.body}>
          {content && (
            <div className={Styles.overviewBlock}>
              <p className={Styles.overviewLabel}>あなたを一言でいうと</p>
              <h2 className={Styles.overviewOneLiner}>{content.oneLiner}</h2>
              {content.closingMessage.map((p, i) => (
                <p key={i} className={Styles.overviewMessage}>{p}</p>
              ))}
            </div>
          )}

          {content && (
            <SectionCard icon={<IoStarOutline size={14} />} title="あなたの3つの強み">
              <p className={Styles.paragraph}>{content.strengthsSummary}</p>
              <div className={Styles.itemList}>
                {content.strengths.map((s) => (
                  <div key={s.title} className={Styles.itemRow}>
                    <p className={Styles.itemTitle}>{s.title}</p>
                    <p className={Styles.itemDesc}>{s.description}</p>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {content && (
            <SectionCard icon={<IoCutOutline size={14} />} title="美容師として輝きやすいシーン">
              <p className={Styles.paragraph}>{content.scenesSummary}</p>
              <div className={Styles.itemList}>
                {content.scenes.map((s) => (
                  <div key={s.title} className={Styles.itemRow}>
                    <p className={Styles.itemTitle}>{s.title}</p>
                    <p className={Styles.itemDesc}>{s.description}</p>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {content && (
            <SectionCard icon={<IoHomeOutline size={14} />} title="力を発揮しやすい環境">
              <div className={Styles.stickerWrap}>
                {content.environment.map((e, i) => {
                  const { tint, accent } = accentFor(i);
                  return (
                    <span key={e.title} className={Styles.sticker} style={{ backgroundColor: tint, color: accent }}>{e.title}</span>
                  );
                })}
              </div>
            </SectionCard>
          )}

          {content && (
            <SectionCard icon={<IoSparklesOutline size={14} />} title="性格を4つの側面から">
              <div className={Styles.accordionList}>
                {content.personalitySections.map((s, i) => {
                  const { tint, accent } = accentFor(i);
                  return (
                    <PersonalityAccordion key={s.title} title={s.title} paragraphs={s.paragraphs} tint={tint} accent={accent} />
                  );
                })}
              </div>
            </SectionCard>
          )}

          {content && (
            <SectionCard icon={<IoCheckmarkCircleOutline size={14} />} title="サロン見学で見るポイント">
              <div>
                {content.visitChecklist.map((item, i) => (
                  <div key={item}>
                    <div className={Styles.checklistRow}>
                      <IoCheckmarkCircleOutline size={18} color={accentFor(i).accent} />
                      <span>{item}</span>
                    </div>
                    {i < content.visitChecklist.length - 1 && <div className={Styles.divider} />}
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {content && (
            <SectionCard icon={<IoAlertCircleOutline size={14} />} title="疲れやすい状況">
              <div className={Styles.itemList}>
                {content.tiringSituations.map((t, i) => (
                  <div key={t.title} className={Styles.tiringRow}>
                    <IoAlertCircleOutline size={16} color={accentFor(i).accent} />
                    <div>
                      <p className={Styles.itemTitle}>{t.title}</p>
                      <p className={Styles.itemDesc}>{t.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {content && (
            <>
              <p className={Styles.compatIntro}>
                相性は「合う・合わない」を決めるものではありません。一緒にいると自然に噛み合いやすいタイプ、自分にないものを補ってくれるタイプ、意識しないとズレやすいタイプとして紹介します。
              </p>

              <SectionCard icon={<IoHeartOutline size={14} color="#3D9A5C" />} title="自然に噛み合いやすい">
                <div className={Styles.compatList}>
                  {content.naturalFit.map((m) => (
                    <CompatibilityCard key={m.type} match={m} tint="#E6F4EA" accent="#3D9A5C" onTap={openIntroduce} />
                  ))}
                </div>
              </SectionCard>

              <SectionCard icon={<IoCompassOutline size={14} color="#3E7BC4" />} title="自分を広げてくれる">
                <div className={Styles.compatList}>
                  {content.broadening.map((m) => (
                    <CompatibilityCard key={m.type} match={m} tint="#E7F0FB" accent="#3E7BC4" onTap={openIntroduce} />
                  ))}
                </div>
              </SectionCard>

              <SectionCard icon={<IoWarningOutline size={14} color="#C98A2E" />} title="意識しないとズレやすい">
                <div className={Styles.compatList}>
                  {content.needsAwareness.map((m) => (
                    <CompatibilityCard key={m.type} match={m} tint="#FBF0DE" accent="#C98A2E" onTap={openIntroduce} />
                  ))}
                </div>
              </SectionCard>
            </>
          )}
        </div>
      </div>
    </DocumentMeta>
  );
};

export default PersonalResult;
